import { useEffect, useState } from "react";
import { useGetDomains } from "../../api/useDomainRobot";
import { Loader } from "../Loader";
import { QueryClientContext } from "../../context/QueryClientContext";
import { lang } from "../../utilities/lang";
import { useCart } from "../../context/CartContext";
import { extractCost, handleCartContent, plausibleEvent } from "../../helpers/helpers";
import { Arrow } from "../../assets/Arrow";
import { Add } from "../../assets/Add";
import { Cart } from "../../assets/Cart";

export type SearchProps = {
    data: {
        query: string | null;
    };
};

export type SearchResultProps = {
    domain: string;
    data: {
        status: string;
        price: string;
        period: string;
        renew: string;
    };
};

export const Search = ({ data }: SearchProps) => {
    return (
        <QueryClientContext>
            <Content data={data} />
        </QueryClientContext>
    );
};

const parseDomain = (domain: string) => {
    return domain.replace(/[^a-zA-Z0-9-]/g, "").replace(/(?<=^..)--/g, "-");
};

const Content = ({ data }: SearchProps) => {
    const [domainSearch, setDomainSearch] = useState<string>(data.query ? parseDomain(data.query) : "");
    const [searchEnabled, setSearchEnabled] = useState<boolean>(data.query !== null);

    const domainQuery = useGetDomains(domainSearch, searchEnabled);

    useEffect(() => {
        if (!domainQuery.every((query) => query.isSuccess)) {
            plausibleEvent("domain-search", domainSearch);
            setSearchEnabled(false);
        }
    }, [domainQuery]);

    return (
        <>
            <div className={"search-input"}>
                <label>
                    <input
                        className={"domain-search"}
                        type={"text"}
                        value={domainSearch}
                        placeholder={lang.get("entry.placeholder")}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                setSearchEnabled(true);
                            }
                        }}
                        onChange={(e) => {
                            setDomainSearch(parseDomain(e.currentTarget.value));
                        }}
                    />
                </label>
            </div>

            <div className={"button-wrapper"}>
                <button className={"button red"} onClick={() => setSearchEnabled(true)}>
                    {lang.get("Domain suchen")}
                </button>
            </div>
            {domainQuery.some((query) => query.isLoading) ? (
                <div className={"loader-wrapper"}>
                    <Loader color={"#008c5e"} />
                </div>
            ) : domainQuery.some((query) => query.isSuccess) ? (
                <div className={"search-result"}>
                    <h1>{lang.get("Suchergebnis")}</h1>
                    <ul className={"result-list"}>
                        {domainQuery.map((query, index) => {
                            if (query.isSuccess && query.data) {
                                const domain = query.data.config.url.split("/").pop();
                                const data = query.data.data;
                                if (data.status === "free" || data.status === "taken") {
                                    return <SearchResult key={index} domain={domain} data={data} />;
                                }
                                return <></>;
                            }
                            return null;
                        })}
                    </ul>
                </div>
            ) : null}
        </>
    );
};

const SearchResult = ({ domain, data }: SearchResultProps) => {
    const [domains, addDomain] = useCart((state) => [state.domains, state.addDomain]);

    const includes = domains.map((d) => d.name).includes(domain);

    return (
        <li className={`result-entry card ${includes ? "in-cart" : ""}`}>
            <span className={"domain"}>{domain}</span>
            <div className={"cost"}>
                <span className={"buy"}>
                    <span>{lang.get("Registrierungskosten")}</span>
                    {extractCost(data.price)}
                </span>
                <span className={"renew"}>
                    <span>{lang.get("Jedes weitere Jahr")}</span>
                    {extractCost(data.renew)}
                </span>
            </div>
            {includes ? (
                <a href={lang.get("checkout.url")} className={"button red inverted domain-action go-to-cart"}>
                    <Cart />
                    {lang.get("Zum Warenkorb")}
                </a>
            ) : (
                <button
                    className={"button red domain-action add-to-cart"}
                    onClick={() => {
                        plausibleEvent("add-to-cart", domain);
                        addDomain({
                            name: domain,
                            price: data.price,
                            renewal: data.renew,
                            status: data.status,
                            duration: data.period,
                        });
                        handleCartContent();
                    }}
                >
                    {data.status === "free" ? (
                        <>
                            <Add />
                            {lang.get("Hinzufügen")}
                        </>
                    ) : (
                        <>
                            <Arrow />
                            {lang.get("Transferieren")}
                        </>
                    )}
                </button>
            )}
        </li>
    );
};
